import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  List,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThread } from '../../context/ThreadContext';
import { useThreadSearch } from '../../hooks/useThreadSearch';
import { Z_INDEX } from '../../constants/zIndex';
import SearchResultItem from './SearchResultItem';

/**
 * SearchModal — Centered modal for searching threads by title + message content.
 * Triggered by sidebar search button or Cmd+K.
 *
 * @param {{ open: boolean, onClose: Function }}
 */
const SearchModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 599px)');
  const navigate = useNavigate();
  const location = useLocation();
  const { selectThread } = useThread();
  const { query, setQuery, results, isSearching, clearSearch } = useThreadSearch();
  const inputRef = useRef(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure dialog is mounted
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      clearSearch();
    }
  }, [open, clearSearch]);

  const handleResultClick = (thread) => {
    selectThread(thread);
    if (location.pathname !== '/') {
      navigate('/');
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      data-testid="search-modal"
      onKeyDown={handleKeyDown}
      sx={{
        zIndex: Z_INDEX.MODALS,
        '& .MuiDialog-paper': {
          borderRadius: 2,
          maxHeight: '60vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search input */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder={t('sidebar.searchModal.placeholder', 'Search by title or content...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            variant="outlined"
            size="small"
            autoComplete="off"
            data-testid="search-input"
            inputProps={{ inputMode: 'search', enterKeyHint: 'search', autoCapitalize: 'none' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch} aria-label="clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {/* Results area */}
        <Box sx={{ minHeight: 100, maxHeight: '45vh', overflowY: 'auto', px: 1, pb: 1 }}>
          {/* Loading */}
          {isSearching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Results list */}
          {!isSearching && results.length > 0 && (
            <List disablePadding>
              {results.map((thread) => (
                <SearchResultItem
                  key={thread.id}
                  thread={thread}
                  snippet={thread.snippet}
                  matchType={thread.matchType}
                  searchTerm={query}
                  onClick={() => handleResultClick(thread)}
                />
              ))}
            </List>
          )}

          {/* No results (with query) */}
          {!isSearching && query.trim() && results.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('sidebar.searchModal.noResults', 'No conversations found')}
              </Typography>
            </Box>
          )}

          {/* Empty state (no query) */}
          {!isSearching && !query.trim() && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="caption" color="text.disabled">
                {t('sidebar.searchModal.shortcutHint', 'Esc to close')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
